import {capitalizeAndRemoveUnderscore} from "helpers/stringHelper";
import Input from "components/form/Input";
import {RadioGroup, RadioItem} from "components/form/RadioGroup";
import CustomButton, {IconButton, OutlinedButton} from "components/CustomButton";
import {ImageUploadCard} from "components/ImageUploadCard";
import BrandSearchSelect from "components/selects/BrandSearchSelect";
import CategorySearchSelect from "components/selects/CategorySearchSelect";
import CustomModal from "components/CustomModal";
import React, { useCallback, useMemo, useRef, useState } from 'react'
import useDidMount from "hooks/useDidMount";
import {
  FOCUS_DISPLAY_TYPE, FOCUS_DISPLAY_TYPE_PRODUCT_VIEW,
  FOCUS_ITEM_STYLE,
  FOCUS_TYPE_OPTIONS, FOCUSED_ITEMS_OTHERS_TYPE, MIN_BANNER_ITEMS,
  MIN_HORIZONTAL_ITEMS,
  PRODUCT_CAMPAIGN_TYPE,
} from '../enums'
import CampaignProductSelect from "components/selects/CampaignProductSelect";
import LabeledSelect from "components/selects/LabeledSelect";
import ProductSearchSelect from "../../../components/selects/ProductSearchSelect";
import DynamicPagesSelect from "../../../components/selects/DynamicPagesSelect";
import FocusedCustomPresets from "../presets/FocusedCustomPresets";
import {calculateLcm} from "../../../helpers/helperFunctions";
import FormLabel from "../../../components/form/FormLabel";
import TitleDisplayToggle from "../presets/TitleDisplayToggle";
import CustomMessage from "components/CustomMessage";
import {UI_FOCUSED_TYPE_BLOG, UI_FOCUSED_TYPE_OTHER} from "../../dynamicUi/constants";
import FocusedOtherSelect, { FocusedOtherPvSelect } from '../../../components/selects/FocusedOtherSelect'
import { createProductUrlValidator, isInternalWebUrl, isValidWebUrl } from '../../../helpers/validationHelper'
import {Tag} from "components/table/Status";
import CustomIcon from "components/CustomIcon";
import useInput from "../../../hooks/useInput";
import {BlogsSearchSelect} from "../../../components/selects/BlogsSearchSelect";
import FocusItemsFormMenu from './FocusItemsFormMenu'

const FocusItemsFormModal = ({showModal, setShowModal, onSubmit, loading}) => {

  const [focusItem, setFocusItem] = useState({});

  const isProductViewDisplayType = focusItem?.display_type === FOCUS_DISPLAY_TYPE[2].value;
  const isGridType = focusItem?.display_type === FOCUS_DISPLAY_TYPE[0].value;
  const isListType = focusItem?.display_type === FOCUS_DISPLAY_TYPE[1].value;
  const isCustomDisplayType = focusItem?.display_type === FOCUS_DISPLAY_TYPE[3].value;

  // Use useRef to store validator lazily
  const productValidatorRef = useRef(null);

  const validateProductUrl = (urlToCheck, supportsEmpty = false) => {
    // Create validator only on first call
    if (!productValidatorRef.current) {
      productValidatorRef.current = createProductUrlValidator();
    }
    return productValidatorRef.current({ url: urlToCheck, supportsEmpty: supportsEmpty  });
  };

   //Precompile validator once using useMemo
  // const productValidator = useMemo(() => createProductUrlValidator(), []);



  const isBrandType = focusItem?.type === FOCUS_TYPE_OPTIONS[0].value;
  const isCategoryType = focusItem?.type === FOCUS_TYPE_OPTIONS[1].value;
  const isCampaignType = focusItem?.type === FOCUS_TYPE_OPTIONS[2].value;
  const isProductType = focusItem?.type === FOCUS_TYPE_OPTIONS[3].value;
  const isPageType = focusItem?.type === FOCUS_TYPE_OPTIONS[4].value;
  const isBlogType = focusItem?.type === UI_FOCUSED_TYPE_BLOG;
  const isOtherType = focusItem?.type === UI_FOCUSED_TYPE_OTHER;
  const isAnyLinkOtherType = (type) => type === FOCUSED_ITEMS_OTHERS_TYPE.anyLink;
  const isNotNoLinkOtherType = (type) => type !== FOCUSED_ITEMS_OTHERS_TYPE.noLink;

  const mainTitle = useInput("");

  useDidMount(() => {
    if (showModal) {
      setFocusItem(showModal);
      mainTitle.setValue(showModal.title ?? "");
    } else {
      setFocusItem({});
    }
  }, [showModal]);
  const extras = focusItem?.extras ?? {};
  const numItems = isGridType ? 2 : isListType ? 1 : isCustomDisplayType ? (extras.rows ?? 3) : 1;
  const numItemsWeb = isGridType ? 4 : isCustomDisplayType ? (extras.rows_web ?? 3) : 2;
  const isBannerStyle = extras.style === FOCUS_ITEM_STYLE.banner;
  const isHorizontalStyle = extras.style === FOCUS_ITEM_STYLE.horizontal;

  const commonMultiple = calculateLcm(numItems, numItemsWeb);

  const isCountValid = isProductViewDisplayType
    || (isBannerStyle || isHorizontalStyle ? (focusItem?.items?.length ?? 0) >= (isBannerStyle ? MIN_BANNER_ITEMS : MIN_HORIZONTAL_ITEMS)
      : ((focusItem?.items?.length ?? 0) % commonMultiple) === 0);

  const isFormInvalid = () => {
    return focusItem.items?.some((i) => !i.value ||
      (isCustomDisplayType && (!i.image_url || !i.image_url_web || ((!i.title?.trim() || !i.sub_title?.trim()) && focusItem.extras.use_image_titles && !isBannerStyle))) ||
      (isOtherType && isAnyLinkOtherType(i.valueType) && (!isValidWebUrl(i.value, true))) ||
      (isOtherType && isNotNoLinkOtherType(i.valueType) && !isAnyLinkOtherType(i.valueType) && !i.value?.trim()) ||
      ((isGridType || isListType) && (!i.title?.trim() || !i.image_url || !i.image_url_web || !i.sub_title?.trim())) ||
      (isProductViewDisplayType  && (!i.title?.trim() || (!i.image_url && i.image_url_web) || (i.image_url && !i.image_url_web) || (!validateProductUrl(i.value) && isOtherType))),
    )
  }

  const isFormReady = isFormInvalid();

  const cannotSubmit = loading || isFormReady || !isCountValid || !mainTitle.value.trim();


  const updateValue = (item, key, keyItem) => {
    if (isProductViewDisplayType && (item[key] || !item.sub_title) && !isCampaignType && !isBrandType) {
      item.title = keyItem.name;
      item.sub_title = "";
    } else if (!isCustomDisplayType && !isProductViewDisplayType && (item[key] || !item.sub_title) && !isProductType) {
      item.sub_title = keyItem.name;
    }

    if (key === 'product') {
      item.value = `${keyItem.product_id}`;
    } else if (isOtherType) {
      console.log(keyItem.value);
      item.value = isNotNoLinkOtherType(keyItem.value) ? (keyItem.edit ?? '') : keyItem.value;
      item.valueType = keyItem.value;
    } else {
      item.value = `${keyItem.id}`;
    }
    item[key] = keyItem;

    setFocusItem(prev => ({...prev, ...focusItem}));
  };

  const updateProductCampaignType = (item, type) => {
    item.campaign_type = type;
    setFocusItem({...focusItem});
  };

  const setDisplayType = (displayType) => {

    if(FOCUS_DISPLAY_TYPE_PRODUCT_VIEW === displayType && focusItem?.items?.length > 1) {
      //restrict only one item in product view
      setFocusItem({...focusItem, display_type: displayType, items: setItems([focusItem.items[0]], displayType)});
    }else{
      setFocusItem({...focusItem, display_type: displayType, items: setItems(focusItem.items, displayType)});
    }

  }

  const setItems = ( items, displayType) => {
    return items.map((item) => displayType === FOCUS_DISPLAY_TYPE_PRODUCT_VIEW && item.value ===FOCUSED_ITEMS_OTHERS_TYPE.noLink ? {...item, value: ''}: item)
  }


  return (
    <CustomModal
      title={showModal ? capitalizeAndRemoveUnderscore(showModal?.type) : ""}
      showModal={showModal}
      setShowModal={setShowModal}
      loading={loading}
      preFooterComponent={isCountValid ? "" :
        <CustomMessage hasBackground={false} message={
          <span>You have selected
            <Tag className="capitalize mr-0 ml-1 font-medium py-0.5 px-1.5"
                 label={`${focusItem?.display_type}${isCustomDisplayType ? `: ${extras.style ?? FOCUS_ITEM_STYLE.normal} style` : ""}`}/>
            .&nbsp;
            {isBannerStyle || isHorizontalStyle ? <span>
            The minimum number of items you should have is {isBannerStyle ? MIN_BANNER_ITEMS : MIN_HORIZONTAL_ITEMS}.
            </span> :
              <span>The number of item count should be the multiple of <span
                className="font-black">{commonMultiple}</span> as per the configuration you have selected.</span>}
        </span>
        }/>}
      button={
        <CustomButton
          className="whitespace-nowrap"
          children="Save All"
          disabled={cannotSubmit}
          onClick={() => !cannotSubmit && onSubmit({...focusItem, title: mainTitle.value.trim()})}
        />
      }
    >
  {!!showModal?.edit && 
          <CustomMessage message="Some fields are read-only and cannot be edited. Please create a new entry if you need to change these values." className="mb-3" />
            }    
      {/*Title section*/}
      <div className="flex flex-col md:flex-row md:space-x-6">
        <Input
          type="text"
          color="lightBlue"
          size="lg"
          value={mainTitle.value}
          onChange={mainTitle.onChange}
          labelComponent={
            <div className="flex items-center justify-between">
              <FormLabel label="Title"/>

              <TitleDisplayToggle focusItem={focusItem} setFocusItem={setFocusItem} show={!isProductViewDisplayType}/>
            </div>
          }
          // isRequired={!isProductViewDisplayType}
          // disabled={isProductViewDisplayType}
          // title={isProductViewDisplayType ? "Title is disabled for the Product Type" : null}
        />
        <div className="w-full">
          <RadioGroup label="Display Type">
            {FOCUS_DISPLAY_TYPE
              .filter((f) => (isBrandType || isCategoryType || isCampaignType || isOtherType) || f.value !== "PRODUCT_VIEW")
              .map((o) => (
                <RadioItem
                  key={`${o.value}_display_type`}
                  label={o.label}
                  isChecked={o.value === focusItem.display_type}
                  name={o.value}
                  value={o.value}
                  disabled={!!showModal?.edit || (isProductType && o === FOCUS_DISPLAY_TYPE[2])}
                  onChanged={() => setDisplayType(o.value)}
                />
              ))}
          </RadioGroup>
        </div>
      </div>
      {isCustomDisplayType && <FocusedCustomPresets preset={focusItem.extras ?? {}} editing={!!showModal?.edit}
                                                    setPreset={(preset) => setFocusItem({
                                                      ...focusItem,
                                                      extras: {...preset, show_title: focusItem.extras.show_title}
                                                    })}/>}
      <FocusItemsFormMenu focusItem={focusItem} setFocusItem={setFocusItem} show={isCustomDisplayType && !isBannerStyle} disabled={!!showModal?.edit}/>

      {focusItem.items?.map((item, index) => (
        <div
          key={`${index}_focusItem_list`}
          className="relative p-4 pt-8 mb-4 border bg-secondary-100 rounded-md"
        >
          {focusItem.items.length > 1 && (
            <div className="absolute top-2 right-2">
              <IconButton
                onClick={() => {
                  const newItems = focusItem.items.filter(
                    (value) => value !== item
                  );
                  setFocusItem({...focusItem, items: newItems});
                }}
              />
            </div>
          )}
          <div className="flex flex-col md:flex-row md:space-x-6">
            <div className="w-full">
              <ImageUploadCard
                title="Mobile"
                initialImage={item.image_url}
                onImageUploaded={(image) => {
                  focusItem.items[index].image_url = image;
                  setFocusItem({...focusItem});
                }}
                onImageRemoved={(image) => {
                  focusItem.items[index].image_url = image;
                  setFocusItem({...focusItem});
                }}
                isRequired={!isProductViewDisplayType}
              />
              <ImageUploadCard
                title="Web"
                initialImage={item.image_url_web}
                onImageUploaded={(image) => {
                  focusItem.items[index].image_url_web = image;
                  setFocusItem({...focusItem});
                }}
                onImageRemoved={(image) => {
                  focusItem.items[index].image_url_web = image;
                  setFocusItem({...focusItem});
                }}
                isRequired={!isProductViewDisplayType}
              />
            </div>
            <div className="w-full">
              {isBrandType ? (
                <BrandSearchSelect
                  label="Select Brand"
                  value={item.brand}
                  extData={item.default}
                  onChange={(brand) => {

                    updateValue(item, 'brand', brand);
                  }}
                />
              ) : isCategoryType ? (
                <CategorySearchSelect
                  label="Search Category"
                  value={item.category}
                  extData={item.default}
                  onChange={(category) => {
                    updateValue(item, 'category', category);
                  }}
                />
              ) : isCampaignType ?
                (
                  <>
                    <LabeledSelect label={"Select Campaign Type"}
                                   value={PRODUCT_CAMPAIGN_TYPE.find((t) => t.value === item.campaign_type)}
                                   options={PRODUCT_CAMPAIGN_TYPE}
                                   placeholder={"Select Campaign Type"}
                                   onChange={(v) => {
                                     updateProductCampaignType(item, v.value);
                                     //reset Everything
                                     updateValue(item, 'product_campaign', {
                                       title: "",
                                       sub_title: "",
                                       name: "",
                                       value: ""
                                     });
                                   }}
                    />
                    {item.campaign_type &&
                      <CampaignProductSelect
                        label="Select Product Campaign"
                        value={item.product_campaign}
                        extData={item.default}
                        onChange={(campaign) => {
                          updateValue(item, 'product_campaign', campaign);
                        }}
                        type={item?.campaign_type}
                      />}
                  </>
                ) :
                isProductType ? (
                  <ProductSearchSelect
                    label="Select Product"
                    value={item.product}
                    extData={item.default}
                    onChange={(product) => {
                      updateValue(item, 'product', product);
                    }}
                  />
                ) : isBlogType ? (
                  <BlogsSearchSelect
                                     value={item.blog || ""}
                                     extData={item.default}
                                     onChange={(page) => {
                                       updateValue(item, 'blog', page);
                                     }}/>
                ) : isPageType ? (
                  <DynamicPagesSelect
                    label="Select a Dynamic Page"
                    value={item.page || ""}
                    extData={item.default}
                    onChange={(page) => {
                      updateValue(item, 'page', page);
                    }}
                  />
                ) : isOtherType ? (

                  isProductViewDisplayType ? <FocusedOtherPvSelect /> :
                    <FocusedOtherSelect
                      label="Select Behaviour"
                      value={item.other || ''}
                      extData={item.default}
                      onChange={(other) => {
                        updateValue(item, 'other', other)
                      }}
                    />

                ) : ''
              }
              <div
                className={`bg-white relative px-3 rounded transition-all grid grid-rows-[0fr] ${isOtherType && ((item.valueType && isNotNoLinkOtherType(item.valueType)) || isProductViewDisplayType) ? "grid-rows-[1fr] pt-3 mb-6 border border-neutral-darker" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                  <Input
                    type="text"
                    label={isProductViewDisplayType ? "Link must be a valid product url." : isAnyLinkOtherType(item.valueType) ? "Link (must be a valid https:// url)" : "Text to be copied"}
                    placeholder={isProductViewDisplayType ? "Product url" : isAnyLinkOtherType(item.valueType) ? "Eg: https://example.com" : "Any Text"}
                    errorMessage={isProductViewDisplayType ?  ( validateProductUrl(item.value, true) ? "" : "Url format not supported. Please copy it from the web app.") : !item.value || isValidWebUrl(item.value, true) || !isAnyLinkOtherType(item.valueType) ? "": "Not a valid url"}
                    // errorMessage={"Hello"}
                    // errorMessage={!item.value || isValidWebUrl(item.value, false) || !isAnyLinkOtherType(item.valueType) ? "": isInternalWebUrl(item.value) ? "Only external links are supported.": "Not a valid url"}
                    value={item.value || ''}
                    onChange={(event) => {
                      if (isAnyLinkOtherType(item.valueType)) {
                        item.value = event.target.value.trim();
                      } else {
                        item.value = event.target.value;
                      }

                      setFocusItem({...focusItem});
                    }}
                  />
                </div>
                <CustomIcon icon="play_arrow"
                            className={`${isOtherType && ((item.valueType && isNotNoLinkOtherType(item.valueType)) || isProductViewDisplayType) ? "block" : "hidden"} text-neutral-darker absolute -rotate-90 -top-[14px] left-10`}
                            size="xl"/>
              </div>

              {((!isCustomDisplayType || focusItem.extras.use_image_titles) && !isBannerStyle) && <Input
                type="text"
                placeholder={isProductViewDisplayType ? "Title" : "Example: Upto 10% Off"}
                value={item.title}
                onChange={(event) => {
                  item.title = event.target.value;
                  setFocusItem({...focusItem});
                }}
                labelComponent={
                  <div className="flex items-center justify-between">
                    <FormLabel label={isProductViewDisplayType ? "Title" : "Sub Title"}/>

                    <TitleDisplayToggle label="Display Title in UI" focusItem={focusItem} setFocusItem={setFocusItem}
                                        show={isProductViewDisplayType} disabled={!!showModal?.edit}/>
                  </div>
                }
              />}
              {(!isProductViewDisplayType && (!isCustomDisplayType || focusItem.extras.use_image_titles) && !isBannerStyle) &&
                <Input
                  type="text"
                  placeholder="Title"
                  value={item.sub_title || ""}
                  onChange={(event) => {
                    item.sub_title = event.target.value;
                    setFocusItem({...focusItem});
                  }}
                />
              }

            </div>
          </div>
        </div>
      ))}
      <div className="flex justify-end">
        <OutlinedButton
          disabled={isFormReady|| isProductViewDisplayType}
          onClick={() => {
            focusItem.items.push({
              title: '',
              sub_title: '',
              value: '',
              image_url: '',
              image_url_web: ''
            });
            setFocusItem({...focusItem});
          }}
        >
          + Add more
        </OutlinedButton>
      </div>
    </CustomModal>
  );
};

export default FocusItemsFormModal;
